// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/bancoIntegracao/providers/interProvider.ts
//
//  Implementação do BancoIntegracaoProvider para o Banco Inter (endpoints
//  /api/inter-*). É a ÚNICA camada que conhece as rotas do Inter — as telas
//  falam só com a interface. Para somar outro banco, crie um provider análogo.
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type { BancoIntegracaoProvider } from '../provider';
import type {
  BancoSaldo, ExtratoSyncResultado, CobrancaOpcoes, CobrancaResultado,
  CobrancaSituacao, CobrancasEmitidasPagina, PixCobRequest, PixCobvRequest, PixCobResultado,
  PixDevolucaoResultado, WebhookTipo,
} from '../types';

export const interProvider: BancoIntegracaoProvider = {
  id: 'inter',
  nome: 'Banco Inter',
  capabilities: { saldo: true, extrato: true, cobranca: true, pix: true, webhooks: true },

  // ── Saldo & Extrato ────────────────────────────────────────────────────────
  async consultarSaldo(contaCorrenteId, data) {
    const { data: r } = await apiClient.get<BancoSaldo>('/api/inter-extrato/saldo', {
      params: { contaCorrenteId, data },
    });
    return r;
  },

  async sincronizarExtrato(contaCorrenteId, dataInicio, dataFim) {
    const { data: r } = await apiClient.post<ExtratoSyncResultado>(
      '/api/inter-extrato/sincronizar',
      { contaCorrenteId, dataInicio, dataFim },
      { timeout: 90_000 },
    );
    return r;
  },

  // ── Cobrança ────────────────────────────────────────────────────────────────
  async emitirCobranca(contaCorrenteId, contaReceberId, opcoes: CobrancaOpcoes) {
    const { data: r } = await apiClient.post<CobrancaResultado>('/api/inter-cobranca/emitir', {
      contaCorrenteId, contaReceberId, opcoes,
    });
    return r;
  },

  async listarCobrancas(contaCorrenteId, filtro) {
    const { data } = await apiClient.get<CobrancasEmitidasPagina>('/api/inter-cobranca', {
      params: {
        contaCorrenteId,
        dtInicio: filtro.dtInicio || undefined,
        dtFim: filtro.dtFim || undefined,
        busca: filtro.busca?.trim() || undefined,
        pagina: filtro.pagina ?? 1,
        tamanhoPagina: filtro.tamanhoPagina ?? 20,
      },
    });
    return data;
  },

  async consultarCobranca(contaCorrenteId, codigoSolicitacao) {
    const { data: r } = await apiClient.get<CobrancaSituacao>(
      `/api/inter-cobranca/${encodeURIComponent(codigoSolicitacao)}`,
      { params: { contaCorrenteId } },
    );
    return r;
  },

  async baixarCobrancaPdf(contaCorrenteId, codigoSolicitacao) {
    const { data } = await apiClient.get<Blob>(
      `/api/inter-cobranca/${encodeURIComponent(codigoSolicitacao)}/pdf`,
      { params: { contaCorrenteId }, responseType: 'blob' },
    );
    return data;
  },

  async cancelarCobranca(contaCorrenteId, codigoSolicitacao, motivo) {
    await apiClient.post(
      `/api/inter-cobranca/${encodeURIComponent(codigoSolicitacao)}/cancelar`,
      { contaCorrenteId, motivo },
    );
  },

  // ── PIX ──────────────────────────────────────────────────────────────────────
  async criarPixImediato(contaCorrenteId, req: PixCobRequest) {
    const { data: r } = await apiClient.post<PixCobResultado>('/api/inter-pix/cob', {
      contaCorrenteId, cob: req,
    });
    return r;
  },

  async criarPixComVencimento(contaCorrenteId, req: PixCobvRequest) {
    const { data: r } = await apiClient.post<PixCobResultado>('/api/inter-pix/cobv', {
      contaCorrenteId, cobv: req,
    });
    return r;
  },

  async consultarPix(contaCorrenteId, txid) {
    const { data: r } = await apiClient.get<PixCobResultado>(
      `/api/inter-pix/${encodeURIComponent(txid)}`,
      { params: { contaCorrenteId } },
    );
    return r;
  },

  async devolverPix(contaCorrenteId, e2eid, valor, motivo): Promise<PixDevolucaoResultado> {
    const { data: r } = await apiClient.post<PixDevolucaoResultado>(
      `/api/inter-pix/${encodeURIComponent(e2eid)}/devolucao`,
      { contaCorrenteId, valor, motivo },
    );
    return r;
  },

  // ── Webhooks ──────────────────────────────────────────────────────────────────
  async registrarWebhook(contaCorrenteId, tipo: WebhookTipo, webhookUrl) {
    await apiClient.put('/api/inter-webhook-admin/registrar', {
      contaCorrenteId, tipo, webhookUrl,
    });
  },

  async consultarWebhook(contaCorrenteId, tipo: WebhookTipo) {
    const { data } = await apiClient.get<{ webhookUrl: string | null }>(
      '/api/inter-webhook-admin/consultar',
      { params: { contaCorrenteId, tipo } },
    );
    return data.webhookUrl;
  },

  async excluirWebhook(contaCorrenteId, tipo: WebhookTipo) {
    await apiClient.delete('/api/inter-webhook-admin/excluir', {
      params: { contaCorrenteId, tipo },
    });
  },
};
